import {isTmcOrderViewModel, OrderViewModel, OrderViewModels} from "../../../models/OrderViewModel";
import {WorkingOrderListOrderBy} from "../models/WorkingOrderListOrderBy";
import {Predicate, Transform, UnaryTransform} from "../../../../types/GenericTypes";
import {isIressOrderModel} from "app/orders/models/iress/IressOrderViewModel";
import {OrderListViewModel, OrderListViewModels, TmcOrderListViewModel} from "../../list/models/OrderListViewModel";

export function openOrders(orders: OrderViewModels): OrderViewModels {
    return orders.filter((o) => o.isOpen);
}

export function completedOrders(orders: OrderViewModels): OrderViewModels {
    return orders.filter((o) => o.isCompleted);
}

export function sortCompletedOrders(orders: OrderListViewModels): OrderListViewModels {
    return [...orders].sort((o1, o2) => o2.updated.localeCompare(o1.updated));
}

export function filterCompletedOrders(orders: OrderListViewModels, includeUnfilled: boolean): OrderListViewModels {
    if (includeUnfilled) {
        return orders;
    } else {
        return orders.filter((o) => !o.isCancelled);
    }
}

export function sortOpenOrders(orders: OrderListViewModels, sorting: WorkingOrderListOrderBy): OrderListViewModels {
    switch (sorting) {
        case WorkingOrderListOrderBy.ASX_CODE:
            return [...orders].sort(sortByAsxCode);
        case WorkingOrderListOrderBy.CREATED:
            return [...orders].sort(sortByCreated);
        case WorkingOrderListOrderBy.LAST_UPDATED:
            return [...orders].sort(sortByLastUpdated);
        case WorkingOrderListOrderBy.ACCOUNT:
            return [...orders].sort(sortByAccount);
        case WorkingOrderListOrderBy.ORDER_TREE:
            return sortByOrderTree(orders);
    }
}

function sortByOrderTree(orders: OrderListViewModels): OrderListViewModels {
    const orderNumberAndOrdersMap = new Map<number, OrderListViewModel[]>();

    orders.forEach(order => {
        const parent = order.parentOrderNumber || order.orderNumber;
        const parentOrders = orderNumberAndOrdersMap.get(parent) || [];
        parentOrders.push(order);
        orderNumberAndOrdersMap.set(parent, parentOrders);
    });

    const models: Array<OrderListViewModel> = [];
    orderNumberAndOrdersMap.forEach(v => models.push(...v));
    return models;
}

function sortByAsxCode(o1: OrderListViewModel, o2: OrderListViewModel): number {
    const codeCompare = o1.code.localeCompare(o2.code);
    if (codeCompare !== 0) {
        return codeCompare;
    } else {
        return o2.created.localeCompare(o1.created);
    }
}

function sortByCreated(o1: OrderListViewModel, o2: OrderListViewModel): number {
    const createdCompare = o2.created.localeCompare(o1.created);
    if (createdCompare !== 0) {
        return createdCompare;
    } else {
        return o1.code.localeCompare(o2.code);
    }
}

function sortByLastUpdated(o1: OrderListViewModel, o2: OrderListViewModel): number {
    const updatedCompare = o2.updated.localeCompare(o1.updated);
    if (updatedCompare !== 0) {
        return updatedCompare;
    } else {
        return o1.code.localeCompare(o2.code);
    }
}

function sortByAccount(o1: OrderListViewModel, o2: OrderListViewModel): number {
    const a1 = o1.account?.number;
    const a2 = o2.account?.number;

    if (a1 == null) {
        return a2 == null ? 0 : 1;
    } else if (a2 == null) {
        return -1;
    } else {
        return a1.localeCompare(a2);
    }
}

export function buildOrderListViewModels(orders: OrderViewModels,
                                         mineOnly: boolean,
                                         filterVal: string,
                                         includeExternal: boolean): OrderListViewModels {
    const isMine = isMineFilter(mineOnly);
    const isExternal = externalFilter(includeExternal);
    const filter = orderFilter(filterVal);

    return orders.map((o) => {
        const show = isMine(o) && filter(o) && isExternal(o);
        return buildModel(o, show);
    });
}

function isMineFilter(mineOnly: boolean): Transform<OrderViewModel, boolean> {
    if (mineOnly) {
        return (o) => o.isMine;
    } else {
        return (o) => true;
    }
}

function externalFilter(includeExternal: boolean): Transform<OrderViewModel, boolean> {
    if (includeExternal) {
        return () => true;
    } else {
        return (o) => o.isInternal;
    }
}

export function buildCompletedOrderListViewModels(orders: OrderViewModels,
                                                  filterVal: string,
                                                  includeUnfilled: boolean,
                                                  mineOnly: boolean,
                                                  includeExternal: boolean): OrderListViewModels {
    const isMine = isMineFilter(mineOnly);
    const isExternal = externalFilter(includeExternal);
    const unfilledFilter = includeUnfilledFilter(includeUnfilled);
    const filter = orderFilter(filterVal);

    return orders.map((o) => {
        const visible = filter(o) && unfilledFilter(o) && isMine(o) && isExternal(o);
        return buildModel(o, visible);
    });
}

function includeUnfilledFilter(includeUnfilled: boolean): Transform<OrderViewModel, boolean> {
    if (includeUnfilled) {
        return () => true;
    } else {
        return (o) => !o.isCancelled;
    }
}

function buildModel(order: OrderViewModel, visible: boolean): OrderListViewModel {
    if (isTmcOrderViewModel(order)) {
        return TmcOrderListViewModel.of(order, visible);
    } else {
        return OrderListViewModel.of(order, visible);
    }
}

function orderFilter(filterValue: string): Predicate<OrderViewModel> {
    if (filterValue == null || filterValue === "") {
        return () => true;
    } else {
        const normalizedFilterValue = filterValue.toLocaleLowerCase();

        return (order) => {
            const accountNameMatch = order?.account?.name.toLocaleLowerCase().includes(normalizedFilterValue);
            const accountNumberMatch = order?.account?.number.toLocaleLowerCase().includes(normalizedFilterValue);
            const securityMatch = order.code.toLocaleLowerCase().includes(normalizedFilterValue);
            const orderNumberMatch = isIressOrderModel(order) && order.orderNumber.toString().includes(
                normalizedFilterValue);

            return accountNameMatch ||
                accountNumberMatch ||
                securityMatch ||
                orderNumberMatch;
        };
    }
}
